import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Save, RotateCcw, Mail, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_ICONS = {
  verify_email:   "✉",
  reset_password: "🔑",
  invitation:     "👋",
};

export default function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", body_text: "" });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data);
      if (!active && data.length > 0) {
        setActive(data[0].key);
        setForm({ subject: data[0].subject, body_text: data[0].body_text });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectTemplate = (t) => {
    setActive(t.key);
    setForm({ subject: t.subject, body_text: t.body_text });
  };

  const activeTemplate = templates.find((t) => t.key === active);

  const save = async () => {
    if (!active) return;
    if (!form.subject.trim() || !form.body_text.trim()) {
      toast.error("Le sujet et le corps ne peuvent pas être vides.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${active}`, form);
      toast.success("Template enregistré.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!active) return;
    if (!window.confirm("Rétablir le template par défaut ?")) return;
    setResetting(true);
    try {
      await api.delete(`/admin/email-templates/${active}`);
      toast.success("Template réinitialisé.");
      await load();
      // Reload form with new default
      const { data } = await api.get("/admin/email-templates");
      const tpl = data.find((t) => t.key === active);
      if (tpl) setForm({ subject: tpl.subject, body_text: tpl.body_text });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-8">Chargement…</div>;

  return (
    <div className="flex gap-6 min-h-[500px]">
      {/* ── Template list ── */}
      <div className="w-52 shrink-0 space-y-1">
        {templates.map((t) => (
          <button
            key={t.key}
            data-testid={`email-tpl-tab-${t.key}`}
            onClick={() => selectTemplate(t)}
            className={`w-full text-left px-4 py-3 border transition-all ${
              active === t.key
                ? "border-l-2 border-l-[#002FA7] border-zinc-200 bg-zinc-50 text-zinc-900"
                : "border border-transparent hover:bg-zinc-50 text-zinc-600"
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail size={13} strokeWidth={1.5} className="shrink-0 text-zinc-400" />
              <span className="text-xs font-medium leading-tight">{t.label}</span>
            </div>
            {t.is_custom && (
              <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-emerald-600">
                <CheckCircle size={9} strokeWidth={2} />
                Personnalisé
              </div>
            )}
          </button>
        ))}
      </div>

      {/* ── Editor ── */}
      {activeTemplate && (
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-0.5">
                {activeTemplate.key}
              </div>
              <div className="font-semibold text-zinc-900">{activeTemplate.label}</div>
            </div>
            <div className="flex items-center gap-2">
              {activeTemplate.is_custom && (
                <button
                  data-testid="email-tpl-reset-btn"
                  onClick={reset}
                  disabled={resetting}
                  className="inline-flex items-center gap-1.5 border border-zinc-300 text-sm px-3 py-1.5 text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  <RotateCcw size={12} strokeWidth={1.5} />
                  Réinitialiser
                </button>
              )}
              <button
                data-testid="email-tpl-save-btn"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-1.5 transition-colors disabled:opacity-50"
              >
                <Save size={12} strokeWidth={2} />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">
              Sujet de l'email
            </label>
            <input
              data-testid="email-tpl-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
            />
          </div>

          {/* Body */}
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-widest font-mono text-zinc-500 mb-1.5">
              Corps du message
            </label>
            <textarea
              data-testid="email-tpl-body"
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              rows={12}
              className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono leading-relaxed focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none resize-y"
            />
          </div>

          {/* Variables hint */}
          <div className="flex items-start gap-2 p-3 bg-zinc-50 border border-zinc-200">
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mt-0.5 shrink-0">
              Variables
            </div>
            <div className="flex flex-wrap gap-1.5">
              {activeTemplate.variables.map((v) => (
                <code
                  key={v}
                  className="text-[11px] font-mono bg-white border border-zinc-200 px-2 py-0.5 text-[#002FA7]"
                >
                  {v}
                </code>
              ))}
              <span className="text-[11px] text-zinc-400 self-center">
                — seront remplacées automatiquement à l'envoi
              </span>
            </div>
          </div>

          {/* Last modified */}
          {activeTemplate.updated_at && (
            <div className="mt-3 text-[11px] text-zinc-400 font-mono">
              Dernière modification : {new Date(activeTemplate.updated_at).toLocaleString("fr-FR")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
